import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { SpotToast } from "../SpotToast";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { useForm, useWatch } from "react-hook-form";
import { ConfigFormRenderer } from "../Form/ConfigFormRenderer";
import axios, { isAxiosError } from "axios";
import { Skeleton } from "../shadcn/skeleton";
import { KeyRound, Link2 } from "lucide-react";
import Image from "next/image";
import type { ProviderInfo } from "@/generated";
import { DeviceAuthConnect } from "@/components/Views/LinkedAccounts/DeviceAuthConnect";
import { OAuthAppForm } from "@/components/Views/LinkedAccounts/OAuthAppForm";
import {
  hasOAuthAppForm,
  isOAuthAppComplete,
} from "@/components/Views/LinkedAccounts/utils";

export interface UseIntegrationsNCLFormProps {
  isActive: boolean;
  onClose: () => void;
  name: string;
  integrationType: string;
  configEndpoint: string;
  submitEndpoint: string;
  submitMethod?: "POST" | "PUT";
  credentialPayloadEndpoint?: string;
  providers?: ProviderInfo[];
  image?: string;
  onOAuthSelect?: (
    provider: ProviderInfo,
    connectionConfig?: Record<string, unknown>
  ) => Promise<boolean>;
}

export interface NCLFormFooterAction {
  label: string;
  variant: "default" | "outline";
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

export interface UseIntegrationsNCLFormResult {
  showPicker: boolean;
  body: React.ReactNode;
  description: string | undefined;
  footerActions: NCLFormFooterAction[];
  handleClose: () => void;
}

export function useIntegrationsNCLForm({
  isActive,
  onClose,
  name,
  integrationType,
  configEndpoint: configEndpointProp,
  submitEndpoint: submitEndpointProp,
  submitMethod = "POST",
  credentialPayloadEndpoint,
  providers,
  image,
  onOAuthSelect,
}: UseIntegrationsNCLFormProps): UseIntegrationsNCLFormResult {
  const queryClient = useQueryClient();
  const isEditMode = submitMethod === "PUT";

  const availableProviders = useMemo(
    () => (providers ?? []).filter((p) => p.available),
    [providers]
  );
  const needsPicker = !isEditMode && availableProviders.length > 1;

  const [selectedProvider, setSelectedProvider] = useState<ProviderInfo | null>(
    null
  );
  const [oauthStarting, setOauthStarting] = useState(false);

  useEffect(() => {
    if (!isActive) {
      setSelectedProvider(null);
      setOauthStarting(false);
    }
  }, [isActive]);

  const autoProvider =
    !needsPicker && availableProviders.length === 1
      ? availableProviders[0]
      : null;

  const activeProvider = selectedProvider ?? autoProvider;
  const configEndpoint = activeProvider?.config_url ?? configEndpointProp;
  const submitEndpoint = activeProvider?.save_url ?? submitEndpointProp;
  const isDeviceProvider = activeProvider?.auth_type === "device_code";
  const isOAuthAppProvider =
    !!activeProvider && hasOAuthAppForm(activeProvider);

  const showPicker = needsPicker && selectedProvider === null;

  const {
    data: configData,
    isLoading: configLoading,
    error: configError,
  } = useQuery({
    queryKey: ["integration-config", integrationType, configEndpoint],
    queryFn: async () => {
      const response = await axios.post(`/api/backend${configEndpoint}`, {});
      return response.data;
    },
    enabled: isActive && !showPicker && !!configEndpoint && !isDeviceProvider,
  });

  const { data: existingPayload, isLoading: payloadLoading } = useQuery({
    queryKey: ["credential-payload", credentialPayloadEndpoint],
    queryFn: async () => {
      const response = await axios.get(
        `/api/backend${credentialPayloadEndpoint}`
      );
      return response.data?.payload as Record<string, unknown> | undefined;
    },
    enabled: isActive && isEditMode && !!credentialPayloadEndpoint,
  });

  const getInitialValues = useMemo(() => {
    const values: Record<string, unknown> = {};

    if (configData?.config) {
      Object.keys(configData.config).forEach((key) => {
        const config = configData.config[key] as { default?: unknown };
        values[key] = config?.default ?? "";
      });
    }

    if (isEditMode && existingPayload) {
      Object.keys(existingPayload).forEach((key) => {
        if (key in values || configData?.config?.[key]) {
          values[key] = existingPayload[key];
        }
      });
    }

    return values;
  }, [configData, isEditMode, existingPayload]);

  const form = useForm({
    defaultValues: getInitialValues,
    mode: "onChange",
  });

  useEffect(() => {
    if (configData?.config) {
      form.reset(getInitialValues);
    }
  }, [configData, existingPayload, form, getInitialValues]);

  const formValues = useWatch({ control: form.control });
  const oauthAppComplete =
    !!configData?.config && isOAuthAppComplete(configData.config, formValues);

  const submitMutation = useMutation({
    mutationFn: async (formData: Record<string, unknown>) => {
      const url = `/api/backend${submitEndpoint}`;
      return submitMethod === "PUT"
        ? axios.put(url, { payload: formData })
        : axios.post(url, formData);
    },
    onSuccess: () => {
      SpotToast.success(`${name} integrated successfully.`);
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.INTEGRATIONS] });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.INTEGRATIONS_CONFIG],
      });
      onClose();
    },
    onError: (error: unknown) => {
      const message = isAxiosError(error)
        ? ((error.response?.data as { message?: string } | undefined)
            ?.message ?? error.message)
        : error instanceof Error
          ? error.message
          : "Unknown error";
      SpotToast.error(`Failed to integrate ${name}: ${message}`);
    },
  });

  const handleSubmit = () => {
    const formData = form.getValues();
    submitMutation.mutate(formData);
  };

  const handleClose = () => {
    form.reset();
    setSelectedProvider(null);
    onClose();
  };

  const handleProviderClick = (provider: ProviderInfo) => {
    if (
      provider.auth_type === "secret_auth" ||
      provider.auth_type === "device_code" ||
      hasOAuthAppForm(provider)
    ) {
      setSelectedProvider(provider);
    } else {
      handleClose();
      onOAuthSelect?.(provider);
    }
  };

  // Stays open until the browser leaves for the provider, so a failed start
  // keeps what the user typed.
  const handleContinueWithOAuth = async () => {
    if (!activeProvider || !onOAuthSelect) return;
    setOauthStarting(true);
    const started = await onOAuthSelect(activeProvider, form.getValues());
    if (!started) setOauthStarting(false);
  };

  const description = showPicker
    ? "Choose an authentication method"
    : isDeviceProvider
      ? (activeProvider?.display_name ?? `Sign in to ${name}.`)
      : isOAuthAppProvider
        ? `Enter your OAuth app, then sign in to ${name}.`
        : configData?.config
          ? `Configure your connection to ${name} below.`
          : undefined;

  const body = showPicker ? (
    <div className="flex flex-col gap-2">
      {availableProviders.map((provider) => (
        <div
          key={provider.key}
          className="flex items-center gap-3 p-3 pr-4 rounded-lg border border-border bg-card shadow-xs cursor-pointer hover:border-ring transition-colors"
          onClick={() => handleProviderClick(provider)}
        >
          <div className="shrink-0 w-9 h-9 rounded-lg bg-background border border-border/40 flex items-center justify-center p-1.5">
            {provider.image ? (
              <Image
                width={24}
                height={24}
                alt={provider.display_name}
                src={provider.image}
                style={{
                  objectFit: "contain",
                  width: "100%",
                  height: "100%",
                }}
              />
            ) : image ? (
              <Image
                width={24}
                height={24}
                alt={name}
                src={image}
                style={{
                  objectFit: "contain",
                  width: "100%",
                  height: "100%",
                }}
              />
            ) : provider.auth_type === "secret_auth" ? (
              <KeyRound size={20} className="text-warning-600" />
            ) : (
              <Link2 size={20} className="text-blue-600" />
            )}
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <span className="font-medium text-sm text-foreground truncate">
              {provider.display_name}
            </span>
            <span className="text-xs text-muted-foreground">
              {provider.auth_type === "secret_auth"
                ? "Connect using API credentials"
                : provider.auth_type === "device_code"
                  ? "Sign in with a one-time code"
                  : `Sign in with your ${name} account with OAuth`}
            </span>
          </div>
        </div>
      ))}
    </div>
  ) : isDeviceProvider && activeProvider ? (
    <DeviceAuthConnect
      integrationType={integrationType}
      displayName={name}
      provider={activeProvider}
      onSuccess={handleClose}
    />
  ) : configLoading || (isEditMode && payloadLoading) ? (
    <div className="space-y-4">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  ) : configError ? (
    <p className="text-sm text-destructive">
      Failed to load configuration. Please try again.
    </p>
  ) : configData?.config && isOAuthAppProvider && activeProvider ? (
    <OAuthAppForm
      provider={activeProvider}
      integrationName={name}
      config={configData.config}
      form={form}
    />
  ) : configData?.config ? (
    <ConfigFormRenderer
      config={configData.config}
      form={form}
      getNodeById={() => undefined}
    />
  ) : (
    <p className="text-sm text-muted-foreground">No configuration required.</p>
  );

  const footerActions: NCLFormFooterAction[] = showPicker
    ? [
        {
          label: "Cancel",
          variant: "outline",
          onClick: handleClose,
        },
      ]
    : isDeviceProvider
      ? [
          {
            label: needsPicker && selectedProvider ? "Back" : "Cancel",
            variant: "outline",
            onClick:
              needsPicker && selectedProvider
                ? () => setSelectedProvider(null)
                : handleClose,
          },
        ]
      : [
          {
            label: needsPicker && selectedProvider ? "Back" : "Cancel",
            variant: "outline",
            onClick:
              needsPicker && selectedProvider
                ? () => setSelectedProvider(null)
                : handleClose,
            disabled: oauthStarting,
          },
          isOAuthAppProvider
            ? {
                label: "Continue with OAuth",
                variant: "default",
                onClick: handleContinueWithOAuth,
                loading: oauthStarting,
                disabled: configLoading || !!configError || !oauthAppComplete,
              }
            : {
                label: submitMethod === "PUT" ? "Save" : "Connect",
                variant: "default",
                onClick: handleSubmit,
                loading: submitMutation.status === "pending",
                disabled: configLoading || !!configError,
              },
        ];

  return {
    showPicker,
    body,
    description,
    footerActions,
    handleClose,
  };
}
